import {
  forwardRef,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react';
import clsx from 'clsx';
import {
  Loading,
  DotColorVariant as LoadingDotColorVariant,
} from '../../loading-primitives/components/Loading';
import { Heading } from '../typography/Heading/Heading';
import { Paragraph } from '../typography/Paragraph/Paragraph';
import styles from './Button.module.css';

export type StyleVariant =
  | 'muted'
  | 'inverted'
  | 'default'
  | 'success'
  | 'warning'
  | 'critical'
  | 'accent';

type AccentButtonStyle =
  | 'grapefruit'
  | 'peanut butter'
  | 'avocado'
  | 'elderberry'
  | 'fig';

// Tertiary and Link are similar, but Link text is underlined and Tertiary is not
export type ProminenceVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'tertiary-underlined';

export type SizeVariant =
  | 'small'
  | 'medium'
  | 'large'
  | 'small-with-icon'
  | 'medium-with-icon'
  | 'large-with-icon';

export type IconPositionVariant = 'left' | 'right';

export type WidthVariant = 'full-width' | 'full-width-mobile';

// New icon props (preferred)
interface NewIconProps {
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

// Legacy icon props (backwards compatibility)
interface LegacyIconProps {
  icon?: ReactNode;
  iconPosition?: IconPositionVariant;
}

// Combined icon props - support both approaches
type ButtonIconProps = NewIconProps & LegacyIconProps;

interface BaseButtonProps {
  testid?: string;
  style: StyleVariant;
  prominence: ProminenceVariant;
  size: SizeVariant;
  className?: string;
  children?: ReactNode;
  width?: WidthVariant;
  // Legacy text props (backwards compatibility)
  textPrimary?: ReactNode;
  centerContent?: boolean;
  loading?: boolean;
  accentColor?: AccentButtonStyle;
  buttonType?: 'link' | 'submit' | 'button' | 'visual';
  disabled?: boolean;
  onClick?:
    | MouseEventHandler<HTMLAnchorElement>
    | MouseEventHandler<HTMLButtonElement>;
  // Declarative analytics opt-in (see @hungryroot/segment trackableProps).
  // Only `data-analytics-*` attributes pass through to the rendered element;
  // the prop surface otherwise stays closed on purpose.
  [key: `data-analytics-${string}`]: string | undefined;
}

interface LinkButtonProps extends BaseButtonProps {
  buttonType: 'link';
  to: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  openLinkInNewTab?: boolean;
}

export interface SubmitButtonProps extends BaseButtonProps {
  buttonType?: 'submit' | 'button';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  form?: string;
}

interface VisualButtonProps extends BaseButtonProps {
  buttonType: 'visual';
}

export type Props = (LinkButtonProps | SubmitButtonProps | VisualButtonProps) &
  ButtonIconProps;

interface GetVariantClassProps {
  style: StyleVariant;
  prominence: ProminenceVariant;
  size: SizeVariant;
  accentStyle: AccentButtonStyle;
  width?: WidthVariant;
  centerContent?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

function getVariantClass({
  style,
  prominence,
  size,
  accentStyle,
  width,
  centerContent,
  loading,
  disabled,
}: GetVariantClassProps) {
  // Use disabled styling when disabled prop is true
  const className = disabled
    ? styles[`disabled-${prominence}`]
    : style !== 'accent'
      ? styles[`${style}-${prominence}`]
      : clsx({
          // Grapefruit
          [styles['grapefruit-tertiary']]:
            accentStyle === 'grapefruit' && prominence === 'tertiary',
          [styles['grapefruit-secondary']]:
            accentStyle === 'grapefruit' && prominence === 'secondary',
          [styles.grapefruit]:
            accentStyle === 'grapefruit' && prominence === 'primary',

          // Peanut butter
          [styles['peanut-butter-tertiary']]:
            accentStyle === 'peanut butter' && prominence === 'tertiary',
          [styles['peanut-butter-secondary']]:
            accentStyle === 'peanut butter' && prominence === 'secondary',
          [styles['peanut-butter']]:
            accentStyle === 'peanut butter' && prominence === 'primary',

          // Avocado
          [styles['avocado-tertiary']]:
            accentStyle === 'avocado' && prominence === 'tertiary',
          [styles['avocado-secondary']]:
            accentStyle === 'avocado' && prominence === 'secondary',
          [styles.avocado]:
            accentStyle === 'avocado' && prominence === 'primary',

          // elderberry
          [styles['elderberry-tertiary']]:
            accentStyle === 'elderberry' && prominence === 'tertiary',
          [styles['elderberry-secondary']]:
            accentStyle === 'elderberry' && prominence === 'secondary',
          [styles.elderberry]:
            accentStyle === 'elderberry' && prominence === 'primary',

          // fig
          [styles['fig-tertiary']]:
            accentStyle === 'fig' && prominence === 'tertiary',
          [styles['fig-secondary']]:
            accentStyle === 'fig' && prominence === 'secondary',
          [styles.fig]: accentStyle === 'fig' && prominence === 'primary',
        });

  return [
    className,
    styles[`${size}`],
    width ? styles[`${width}`] : '',
    centerContent || centerContent === undefined
      ? styles['center']
      : styles['space-between'],
    loading ? styles['loading'] : '',
  ].join(' ');
}

const getButtonTextVariant = (size?: SizeVariant) => {
  switch (size) {
    case 'small':
    case 'small-with-icon':
      return 'h7';
    case 'medium':
    case 'medium-with-icon':
      return 'H6';
    case 'large':
    case 'large-with-icon':
      return 'H5';
  }
  return 'h7';
};

function getButtonContent(
  // New API props
  children?: ReactNode,
  iconLeft?: ReactNode,
  iconRight?: ReactNode,
  // Legacy API props (backwards compatibility)
  textPrimary?: ReactNode,
  icon?: ReactNode,
  iconPosition?: IconPositionVariant,
  testid?: string,
  size?: SizeVariant,
  // Control whether to wrap text in heading/paragraph elements
  wrapTextInElement: boolean = true
) {
  const buttonTextVariant = getButtonTextVariant(size);

  // Determine what text to use: prefer children, fall back to textPrimary
  const primaryText = children || textPrimary;

  // Determine what icons to use: prefer new iconLeft/iconRight, fall back to legacy icon+iconPosition
  // Only use legacy props if NO new icon props are provided
  const hasNewIconProps = iconLeft !== undefined || iconRight !== undefined;
  const leftIcon = hasNewIconProps
    ? iconLeft
    : iconPosition === 'left'
      ? icon
      : undefined;
  const rightIcon = hasNewIconProps
    ? iconRight
    : iconPosition === 'right'
      ? icon
      : undefined;

  const renderText = (text: ReactNode) => {
    if (!text) return null;

    // For visual buttons, return text directly without wrapper to avoid invalid HTML
    if (!wrapTextInElement) {
      return text;
    }

    // Only wrap primitive text. If a React element is provided
    // we assume the caller has handled the styling
    if (typeof text === 'string' || typeof text === 'number') {
      return buttonTextVariant === 'h7' ? (
        <Paragraph variant={buttonTextVariant}>{text}</Paragraph>
      ) : (
        <Heading variant={buttonTextVariant}>{text}</Heading>
      );
    }

    return text;
  };

  const renderIconLeft = () => {
    if (!leftIcon) return null;

    return (
      <span data-testid={`${testid}-icon-left`} className={styles['icon-left']}>
        {leftIcon}
      </span>
    );
  };

  const renderIconRight = () => {
    if (!rightIcon) return null;

    return (
      <span
        data-testid={`${testid}-icon-right`}
        className={styles['icon-right']}
      >
        {rightIcon}
      </span>
    );
  };

  // If only icons and no text, render just the icons
  if (!primaryText && (leftIcon || rightIcon)) {
    return (
      <>
        {renderIconLeft()}
        {renderIconRight()}
      </>
    );
  }

  // Standard case: text with optional icons
  return (
    <>
      {renderIconLeft()}
      {renderText(primaryText)}
      {renderIconRight()}
    </>
  );
}

function getLoadingContent(
  loading?: boolean,
  testid?: string,
  prominence?: ProminenceVariant,
  style?: StyleVariant,
  size?: SizeVariant
) {
  if (loading) {
    const isNotLarge = size !== 'large' && size !== 'large-with-icon';

    if (prominence === 'primary') {
      if (style === 'inverted') {
        return (
          <Loading
            testid={`${testid}-loading`}
            variant="inline-button"
            dotColor={LoadingDotColorVariant.ColorNeutral500}
            sizeVariant={isNotLarge ? 'small' : undefined}
          />
        );
      }
      return (
        <Loading
          testid={`${testid}-loading`}
          variant="inline-button"
          dotColor={LoadingDotColorVariant.ColorNeutral0}
          sizeVariant={isNotLarge ? 'small' : undefined}
        />
      );
    } else {
      return (
        <Loading
          variant="inline-button"
          sizeVariant={isNotLarge ? 'small' : undefined}
        />
      );
    }
  }
}

const getVisualVariantTypeButtonContent = ({
  loading,
  testid,
  prominence,
  style,
  size,
  children,
  textPrimary,
  iconLeft,
  iconRight,
  icon,
  iconPosition,
}: {
  loading?: boolean;
  testid?: string;
  prominence?: ProminenceVariant;
  style?: StyleVariant;
  size?: SizeVariant;
  children?: ReactNode;
  textPrimary?: ReactNode;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  icon?: ReactNode;
  iconPosition?: IconPositionVariant;
}) => {
  if (loading) {
    return getLoadingContent(loading, testid, prominence, style, size);
  }

  // For visual buttons, use the same content logic but without text wrapping
  // to avoid invalid HTML (block elements inside inline span element)
  return getButtonContent(
    children,
    iconLeft,
    iconRight,
    textPrimary,
    icon,
    iconPosition,
    testid,
    size,
    false // wrapTextInElement = false for visual buttons
  );
};

export const Button = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement | HTMLAnchorElement>) => {
    const {
      testid,
      style,
      prominence,
      size,
      className,
      iconLeft,
      iconRight,
      icon,
      iconPosition,
      width,
      textPrimary,
      centerContent,
      loading,
      disabled,
      children,
      accentColor: accentStyle = 'grapefruit',
    } = props;

    const analyticsAttributes: Record<string, string> = {};
    for (const [key, value] of Object.entries(props)) {
      if (key.startsWith('data-analytics-') && typeof value === 'string') {
        analyticsAttributes[key] = value;
      }
    }

    if (props.buttonType === 'link') {
      const { to, openLinkInNewTab, onClick } = props;
      const variantClass = getVariantClass({
        style,
        prominence,
        size,
        accentStyle,
        width,
        centerContent,
        disabled,
      });

      return (
        <a
          {...analyticsAttributes}
          {...(disabled ? {} : { href: to })}
          target={openLinkInNewTab ? '_blank' : '_self'}
          ref={ref as Ref<HTMLAnchorElement>}
          data-testid={testid}
          className={`${variantClass} ${className || ''}`}
          onClick={disabled ? undefined : onClick}
        >
          {getButtonContent(
            children,
            iconLeft,
            iconRight,
            textPrimary,
            icon,
            iconPosition,
            testid,
            size
          )}
        </a>
      );
    }

    if (props.buttonType === 'visual') {
      const variantClass = getVariantClass({
        style,
        prominence,
        size,
        accentStyle,
        width,
        centerContent,
        disabled,
      });

      return (
        <span
          {...analyticsAttributes}
          data-testid={testid}
          className={`${variantClass} ${className || ''}`}
        >
          {getVisualVariantTypeButtonContent({
            loading,
            testid,
            prominence,
            style,
            size,
            children,
            textPrimary,
            iconLeft,
            iconRight,
            icon,
            iconPosition,
          })}
        </span>
      );
    }

    const variantClass = getVariantClass({
      style,
      prominence,
      size,
      accentStyle,
      width,
      centerContent,
      loading,
      disabled,
    });

    return (
      <button
        {...analyticsAttributes}
        ref={ref as Ref<HTMLButtonElement>}
        data-testid={testid}
        className={`${variantClass} ${className || ''}`}
        onClick={disabled ? undefined : props.onClick}
        type={props.buttonType}
        disabled={disabled || loading}
        form={props.buttonType === 'submit' ? props.form : undefined}
      >
        {getLoadingContent(loading, testid, prominence, style, size)}

        {getButtonContent(
          children,
          iconLeft,
          iconRight,
          textPrimary,
          icon,
          iconPosition,
          testid,
          size
        )}
      </button>
    );
  }
);
